/**
 * Shared hook for contact edit form state, handlers, and validation.
 * Used by AddContactModal and ContactView edit/create modes.
 */

import { useState, useEffect } from 'react';
import { useAppToast } from '@/hooks/useAppToast';
import { Contact, Phone } from '@/modules/contact';
import { PhoneLogic } from '@/modules/phone';
import type { PhoneTypeKind } from '@/modules/phone';
import { generateUUID } from '@/util/uuid';

export interface ValidatedContactFields {
  prefix: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  nickname: string;
  companyName: string;
  tels: Phone[];
  listingCategories: string[];
  fnPassthru: string | undefined;
}

export interface ContactEditFormState {
  // Name fields
  prefix: string;
  setPrefix: (v: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  middleName: string;
  setMiddleName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  suffix: string;
  setSuffix: (v: string) => void;
  nickname: string;
  setNickname: (v: string) => void;
  companyName: string;
  setCompanyName: (v: string) => void;
  // Phone fields
  tels: Phone[];
  readOnlyTels: Phone[];
  phoneInputs: Record<string, string>;
  handlePhoneChange: (localId: string, value: string) => void;
  handlePhoneTypeChange: (localId: string, kind: PhoneTypeKind) => void;
  handleAddPhone: () => void;
  handleRemovePhone: (localId: string) => void;
  handleRemoveReadOnlyPhone: (localId: string) => void;
  // Listing categories
  listingCategories: string[];
  handleAddListingCategory: (category: string) => void;
  handleRemoveListingCategory: (index: number) => void;
  // Optional fields
  visibleFields: Set<string>;
  showAddFieldSheet: boolean;
  setShowAddFieldSheet: (v: boolean) => void;
  handleAddField: (fieldName: string) => void;
  getAvailableFields: () => { key: string; label: string }[];
  // Derived
  hasValidPhone: boolean;
  // Submit: validates, then calls onSuccess with validated fields (or shows error toast)
  submit: (onSuccess: (fields: ValidatedContactFields) => void) => void;
}

/**
 * @param contact - The contact to edit (source of initial values)
 * @param isActive - When this flips to true, form state is reset from contact
 */
export function useContactEditForm(
  contact: Contact,
  isActive: boolean
): ContactEditFormState {
  const [presentToast] = useAppToast();

  const [prefix, setPrefix] = useState(contact.prefix);
  const [firstName, setFirstName] = useState(contact.firstName);
  const [middleName, setMiddleName] = useState(contact.middleName);
  const [lastName, setLastName] = useState(contact.lastName);
  const [suffix, setSuffix] = useState(contact.suffix);
  const [nickname, setNickname] = useState(contact.nickname);
  const [companyName, setCompanyName] = useState(contact.companyName ?? '');
  const [tels, setTels] = useState<Phone[]>([]);
  const [readOnlyTels, setReadOnlyTels] = useState<Phone[]>([]);
  const [listingCategories, setListingCategories] = useState<string[]>([]);
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const [showAddFieldSheet, setShowAddFieldSheet] = useState(false);
  const [phoneInputs, setPhoneInputs] = useState<Record<string, string>>({});

  // Reset form state from contact whenever isActive becomes true
  useEffect(() => {
    if (isActive) {
      setPrefix(contact.prefix);
      setFirstName(contact.firstName);
      setMiddleName(contact.middleName);
      setLastName(contact.lastName);
      setSuffix(contact.suffix);
      setNickname(contact.nickname);
      setCompanyName(contact.companyName ?? '');
      const editablePhones = contact.tels.filter(t =>
        PhoneLogic.isPartialUSPhone(t.value)
      );
      const nonUSPhones = contact.tels.filter(
        t => !PhoneLogic.isPartialUSPhone(t.value)
      );
      setTels(
        editablePhones.length > 0
          ? editablePhones
          : [
              {
                localId: generateUUID(),
                value: '',
                type: PhoneLogic.PHONE_TYPES.cell,
              },
            ]
      );
      setReadOnlyTels(nonUSPhones);
      setListingCategories([...contact.listingCategories]);

      const fieldsToShow = new Set<string>();
      if (contact.prefix.trim()) fieldsToShow.add('prefix');
      if (contact.middleName.trim()) fieldsToShow.add('middleName');
      if (contact.suffix.trim()) fieldsToShow.add('suffix');
      if (contact.nickname.trim()) fieldsToShow.add('nickname');
      if (contact.companyName?.trim()) fieldsToShow.add('companyName');
      setVisibleFields(fieldsToShow);
    }
  }, [contact, isActive]);

  // Sync phoneInputs when tels change while active
  useEffect(() => {
    if (isActive) {
      const inputs: Record<string, string> = {};
      tels.forEach(tel => {
        inputs[tel.localId] = PhoneLogic.formatForInput(tel.value);
      });
      setPhoneInputs(inputs);
    }
  }, [tels, isActive]);

  const hasValidPhone = Object.values(phoneInputs).some(value =>
    PhoneLogic.isValidUS({
      value,
      localId: '',
      type: PhoneLogic.PHONE_TYPES.cell,
    })
  );

  const handlePhoneChange = (localId: string, formattedPhone: string) => {
    setPhoneInputs(prev => ({ ...prev, [localId]: formattedPhone }));
  };

  const handlePhoneTypeChange = (localId: string, kind: PhoneTypeKind) => {
    const type =
      kind === 'other'
        ? PhoneLogic.makeOther('other')
        : PhoneLogic.PHONE_TYPES[kind];
    setTels(prev =>
      prev.map(tel => (tel.localId === localId ? { ...tel, type } : tel))
    );
  };

  const handleAddPhone = () => {
    const newLocalId = generateUUID();
    const newTel: Phone = {
      localId: newLocalId,
      value: '',
      type: PhoneLogic.PHONE_TYPES.cell,
    };
    setTels(prev => [...prev, newTel]);
    setPhoneInputs(prev => ({ ...prev, [newLocalId]: '' }));
  };

  const handleRemovePhone = (localId: string) => {
    if (tels.length <= 1) {
      presentToast({
        message: 'Contact must have at least one phone number',
        duration: 2000,
        color: 'warning',
      });
      return;
    }
    setTels(prev => prev.filter(tel => tel.localId !== localId));
    setPhoneInputs(prev => {
      const updated = { ...prev };
      delete updated[localId];
      return updated;
    });
  };

  const handleRemoveReadOnlyPhone = (localId: string) => {
    setReadOnlyTels(prev => prev.filter(tel => tel.localId !== localId));
  };

  const handleAddListingCategory = (category: string) => {
    const trimmed = category.trim();
    if (!trimmed) return;
    if (listingCategories.includes(trimmed)) {
      presentToast({
        message: 'Already in this list',
        duration: 2000,
        color: 'warning',
      });
      return;
    }
    setListingCategories(prev => [...prev, trimmed]);
  };

  const handleRemoveListingCategory = (index: number) => {
    setListingCategories(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddField = (fieldName: string) => {
    setVisibleFields(prev => new Set(prev).add(fieldName));
    setShowAddFieldSheet(false);
  };

  const getAvailableFields = () => {
    const allFields = [
      { key: 'prefix', label: 'Prefix' },
      { key: 'middleName', label: 'Middle Name' },
      { key: 'suffix', label: 'Suffix' },
      { key: 'nickname', label: 'Nickname' },
      { key: 'companyName', label: 'Company' },
    ];
    return allFields.filter(field => !visibleFields.has(field.key));
  };

  const submit = (onSuccess: (fields: ValidatedContactFields) => void) => {
    if (tels.length === 0) {
      presentToast({
        message: 'Contact must have at least one phone number',
        duration: 2000,
        color: 'danger',
      });
      return;
    }

    const validatedTels: Phone[] = [];
    for (const tel of tels) {
      const localId = tel.localId;
      const formattedValue = phoneInputs[localId] || '';
      const normalized = PhoneLogic.normalize(formattedValue);

      if (normalized.length !== 10) {
        presentToast({
          message: `Phone number "${formattedValue}" is invalid. Must be 10 digits.`,
          duration: 3000,
          color: 'danger',
        });
        return;
      }

      const originalNormalized = PhoneLogic.normalize(tel.value);
      const value =
        normalized === originalNormalized
          ? tel.value
          : PhoneLogic.format(normalized);

      validatedTels.push({ ...tel, value });
    }

    const allTels = [...validatedTels, ...readOnlyTels];

    const trimmedFields = {
      prefix: prefix.trim(),
      firstName: firstName.trim(),
      middleName: middleName.trim(),
      lastName: lastName.trim(),
      suffix: suffix.trim(),
      nickname: nickname.trim(),
      companyName: companyName.trim(),
      listingCategories: listingCategories
        .map(c => c.trim())
        .filter(c => c.length > 0),
    };

    const nameChanged =
      trimmedFields.prefix !== contact.prefix ||
      trimmedFields.firstName !== contact.firstName ||
      trimmedFields.middleName !== contact.middleName ||
      trimmedFields.lastName !== contact.lastName ||
      trimmedFields.suffix !== contact.suffix;
    const fnPassthru = nameChanged ? undefined : contact.fnPassthru;

    onSuccess({
      ...trimmedFields,
      tels: allTels,
      fnPassthru,
    });
  };

  return {
    prefix,
    setPrefix,
    firstName,
    setFirstName,
    middleName,
    setMiddleName,
    lastName,
    setLastName,
    suffix,
    setSuffix,
    nickname,
    setNickname,
    companyName,
    setCompanyName,
    tels,
    readOnlyTels,
    phoneInputs,
    handlePhoneChange,
    handlePhoneTypeChange,
    handleAddPhone,
    handleRemovePhone,
    handleRemoveReadOnlyPhone,
    listingCategories,
    handleAddListingCategory,
    handleRemoveListingCategory,
    visibleFields,
    showAddFieldSheet,
    setShowAddFieldSheet,
    handleAddField,
    getAvailableFields,
    hasValidPhone,
    submit,
  };
}
